/**
 * Decodes a JWT token and extracts its claims
 * Note: This only decodes the token, it does NOT verify the signature.
 * Signature verification must happen on the server side.
 */
export function decodeJwt(token: string): { [key: string]: any } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }

    const decoded = JSON.parse(atob(parts[1]));
    return decoded;
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}

/**
 * Extracts the user claims from a JWT token
 * Returns sub (user id), name, and role ID from the token
 */
export function extractUserClaimsFromJwt(token: string): {
  sub: string;
  name: string;
  roleId: number;
} | null {
  const decoded = decodeJwt(token);
  if (!decoded) {
    return null;
  }

  const sub = decoded.sub || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
  const name = decoded.name || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
  const roleValue = decoded.roleId || decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

  if (!sub || !name || roleValue === undefined) {
    console.error(`Missing required claims in JWT `);
    return null;
  }

  // Convert role to number if it's a string
  const roleId = typeof roleValue === "string" ? parseInt(roleValue, 10) : roleValue;

  if (isNaN(roleId)) {
    console.error("Invalid role value in JWT");
    return null;
  }

  return { sub, name, roleId };
}

/**
 * Checks if a JWT token is expired
 */
export function isJwtExpired(token: string): boolean {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  const now = Date.now() / 1000;
  return decoded.exp < now;
}
